/**
 * Session Management Types
 * Defines the state machine for authentication and session handling
 */

/**
 * Session status represents the current state of the authentication session
 * This follows a strict state machine with defined transitions
 */
export type SessionStatus =
  | "idle" // Initial state, no session check yet
  | "initializing" // Checking for existing session
  | "hydrating" // Loading persisted data from storage
  | "authenticating" // User actively logging in
  | "loading_profile" // Fetching user profile data
  | "authenticated" // Fully authenticated with complete profile
  | "unauthenticated" // No session exists
  | "error"; // Error state with retry capability

/**
 * Error types that can occur during session management
 */
export type SessionErrorType =
  | "network_error" // Network failure
  | "timeout" // Operation timed out
  | "invalid_token" // Token is invalid
  | "profile_load_failed" // Failed to load user profile
  | "permission_denied" // User doesn't have required permissions
  | "unknown"; // Unknown error

/**
 * Session error object with retry capability
 */
export interface SessionError {
  type: SessionErrorType;
  message: string;
  retryable: boolean;
  retryCount: number;
  originalError?: Error;
  timestamp: number;
}

/**
 * Valid state transitions in the session state machine
 */
export const SESSION_TRANSITIONS: Record<SessionStatus, SessionStatus[]> = {
  idle: ["initializing", "hydrating", "authenticating", "loading_profile"],
  initializing: [
    "authenticating",
    "unauthenticated",
    "error",
    "loading_profile",
  ],
  hydrating: ["authenticated", "loading_profile", "error", "unauthenticated"],
  authenticating: ["loading_profile", "error"],
  loading_profile: ["authenticated", "error"],
  authenticated: ["unauthenticated", "error"],
  unauthenticated: ["authenticating", "initializing", "loading_profile"],
  error: ["initializing", "unauthenticated", "authenticating"],
};

/**
 * Check if a state transition is valid
 */
export function isValidTransition(
  from: SessionStatus,
  to: SessionStatus
): boolean {
  return SESSION_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Configuration for session management
 */
export interface SessionConfig {
  // Maximum age of cached session data (in milliseconds)
  maxSessionAge: number;
  // Timeout for API calls (in milliseconds)
  apiTimeout: number;
  // Maximum loading time before showing error (in milliseconds)
  maxLoadingTime: number;
  // Maximum retry attempts
  maxRetries: number;
  // Initial retry delay (in milliseconds)
  retryDelay: number;
  // Enable debug logging
  debug: boolean;
}

/**
 * Default session configuration
 */
export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  maxSessionAge: 5 * 60 * 1000, // 5 minutes
  apiTimeout: 20000, // 20 seconds (handles slow networks and API responses)
  maxLoadingTime: 25000, // 25 seconds (proportional to API timeout)
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  debug: process.env.NODE_ENV === "development",
};
